// BILLING-INVOICE-EMAIL-001 — Send Invoice modal. To/CC are read-only (Admin-configured
// Billing Email / Billing CC, resolved server-side); Subject + Message editable; snapshot
// Excel attached automatically by the backend.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, Send, Paperclip, AlertTriangle, Settings2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { useAuth } from '../../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v) || 0);

const Field = ({ label, children, testId }) => (
  <div className="grid grid-cols-[72px_1fr] items-start gap-3" data-testid={testId}>
    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

export const SendInvoiceModal = ({ invoice, onClose, onSent }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/pricing/billing/invoice/${invoice.id}/email-draft`)
      .then((r) => { if (!cancelled) { setDraft(r.data); setSubject(r.data.subject || ''); setBody(r.data.body || ''); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load the email draft.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoice.id]);

  const configured = Boolean(draft?.billing_email_configured);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/pricing/billing/invoice/${invoice.id}/send`, { subject, body });
      toast.success(`✓ Invoice sent successfully to ${r.data.sent_to}`);
      onSent?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invoice email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="send-invoice-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-slate-900"><Mail className="w-4 h-4 text-indigo-600" /> Send Invoice</DialogTitle>
          <DialogDescription className="tabular-nums" data-testid="send-invoice-summary">
            Invoice <span className="font-mono font-semibold text-slate-800">{invoice.invoice_number}</span>
            {' · '}Amount <span className="font-semibold text-slate-800">{money(draft?.amount ?? invoice.total_amount)}</span>
            {draft?.period ? <> · Period {draft.period}</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6" data-testid="send-invoice-loading"><Loader2 className="w-4 h-4 animate-spin" /> Preparing draft…</div>
          ) : error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="send-invoice-error">{error}</div>
          ) : (
            <>
              {!configured && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex items-start gap-2" data-testid="billing-email-missing">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold">Billing email is not configured for this customer.</div>
                    <div className="text-xs mt-0.5">
                      {isAdmin ? 'Configure the Billing Email under Admin → Client Codes → Billing.' : 'Ask an Admin to configure the Billing Email under Admin → Client Codes → Billing.'}
                    </div>
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { onClose(); navigate('/admin/clients'); }} data-testid="configure-billing-email-btn">
                      <Settings2 className="w-3.5 h-3.5 mr-1" /> Configure
                    </Button>
                  )}
                </div>
              )}
              <Field label="To" testId="send-invoice-to">
                <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-800 truncate" title="Admin-configured Billing Email (read-only)">
                  {draft.to || <span className="text-slate-400 font-sans">— not configured</span>}
                </div>
              </Field>
              <Field label="CC" testId="send-invoice-cc">
                <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-800 truncate" title="Admin-configured Billing CC (read-only)">
                  {draft.cc || <span className="text-slate-400 font-sans">—</span>}
                </div>
              </Field>
              <Field label="Subject" testId="send-invoice-subject">
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 text-sm" data-testid="send-invoice-subject-input" />
              </Field>
              <Field label="Message" testId="send-invoice-body">
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="text-sm leading-relaxed font-sans" data-testid="send-invoice-body-input" />
              </Field>
              <Field label="Attach" testId="send-invoice-attachment">
                <div className="h-9 flex items-center gap-2 px-3 rounded-md border border-dashed border-slate-300 bg-white text-sm text-slate-700">
                  <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-mono truncate">{draft.attachment_name}</span>
                  <span className="text-xs text-slate-400 ml-auto">Excel · generated from the invoice snapshot</span>
                </div>
              </Field>
              {draft.last_emailed_at && (
                <p className="text-[11px] text-slate-500 pl-[84px]" data-testid="send-invoice-last-sent">Previously sent to {draft.last_emailed_to} on {new Date(draft.last_emailed_at).toLocaleString()}</p>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="send-invoice-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={loading || sending || !configured || !subject.trim() || !body.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="send-invoice-submit-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendInvoiceModal;
